<template>
  <!--Attachment Modal -->
  <b-modal
    id="attachmentTreeModal"
    ref="attachmentTreeModal"
    size="xxl"
    modal-class="attachmentTreeModal"
    centered
    static
    hide-footer
  >
    <template #modal-title>
      <div v-if="associatePictures">
        {{ $t('global.description.caseAttachments') }}
      </div>
      <div v-if="!associatePictures">
        {{ $tc('global.file', 2) }}
      </div>
    </template>
    <b-row
      class="attach-body pl-3"
      style="height:100%;"
    >
      <b-col
        cols="3"
        lg="2"
        class="tree p-0 right-bordered"
      >
        <b-row class="py-1 tree-content-attachments">
          <b-col class="pl-4 pr-1">
            <patient-tree
              id="patientTree"
              ref="patientTree"
              :laboratory-id="laboratoryId"
              :patient-id="patient.patId || patient.id"
              :patient="patient"
              @patient-tree-error="hide"
              @child-selected="patientTreeElementSelected($event)"
            />
          </b-col>
        </b-row>
      </b-col>
      <b-col class="content pl-0">
        <b-container>
          <b-row>
            <b-col class="attach-header bottom-bordered py-2 d-flex justify-content-between pr-2">
              <div class="d-flex align-items-center">
                <div
                  class="text-center"
                  style="width: 25px"
                >
                  <i
                    class="primary-dark-color"
                    :class="`far ${selectedTreeElement.elementIcon} mr-2 main`"
                    aria-hidden="true"
                  />
                </div>
                <span
                  qa="attachment-modal-tree-entity-number"
                  class="ap-title ap-title__section ml-2"
                >{{ selectedTreeElement.entityDisplay }}</span>
                <vtBubble
                  v-if="selectedTreeElement.attachments.length"
                  :tooltip="' '+$tc('global.file',2)"
                  :number="selectedTreeElement.attachments.length"
                  :type="false"
                />
              </div>
              <div
                v-if="selectedTreeElement.userHasAttachOperation"
                class="d-flex align-items-center"
              >
                <b-button
                  v-tooltip="$t('component.attachModal.deleteAttach')"
                  :disabled="!canBeDeleted"
                  class="btn__icon btn__icon--primary px-2"
                  qa="attachment-modal-tree-delete-button"
                  @click="deleteAttachmentConfirmation"
                >
                  <i
                    class="far fa-trash-alt"
                    aria-hidden="true"
                  />
                </b-button>
                <b-button
                  v-tooltip="$t('global.description.modifyFileName')"
                  :disabled="!canBeEdited"
                  class="btn__icon btn__icon--primary px-2"
                  qa="attachment-modal-tree-update-button"
                  @click="showUpdateFileNameModal()"
                >
                  <i
                    class="far fa-edit"
                    aria-hidden="true"
                  />
                </b-button>
                <b-button
                  v-tooltip="$t('component.attachModal.addAttach')"
                  :disabled="!canBeAttached"
                  class="btn__icon btn__icon--primary px-2 mr-3"
                  qa="attachment-modal-tree-upload-button"
                  @click="showAddAttachmentModal"
                >
                  <i
                    class="far fa-paperclip"
                    aria-hidden="true"
                  />
                </b-button>
              </div>
            </b-col>
          </b-row>
        </b-container>
        <div
          class="thumb-list px-3"
          qa="attachment-modal-tree-container"
          :class="{'thumb-list-pictures':associatePictures}"
        >
          <b-row class="py-3 pr-3">
            <vt-thumbnail
              v-for="(attachment) in selectedTreeElement.attachments"
              id="attachmentThumbnail"
              :key="attachment.attachmentId"
              ref="attachmentThumbnail"
              :laboratory-id="laboratoryId"
              :case-id="selectedTreeElement.caseId"
              :patient-id="selectedTreeElement.patientId"
              :sample-id="selectedTreeElement.sampleId"
              :portion-id="selectedTreeElement.portionId"
              :subportion-id="selectedTreeElement.subportionId"
              :complementary-id="selectedTreeElement.complementaryId"
              :sample-label="selectedTreeElement.entityLabel"
              :attachment="attachment"
              @attachmentChecked="getAttachmentsChecked"
              @attachmentSelected="openAttachment($event)"
            />
          </b-row>
        </div>
        <div
          v-if="associatePictures"
          class="thumb-footer text-right p-3"
        >
          <b-button
            class="btn--primary"
            size="sm"
          >
            {{ $t('global.description.addToSection') }} (2)
          </b-button>
        </div>
      </b-col>
    </b-row>

    <modal-wrapper ref="addFileModalWrapper">
      <add-file-modal
        id="addFileModal"
        ref="addFileModal"
        :attachment-entity-type="selectedTreeElement.attachmentEntityType"
        :entities-id-array="entitiesIdArray"
        @attachmentsAdded="onAttachmentsAdded($event)"
      />
    </modal-wrapper>
    <modal-confirmation
      id="attachmentsConfirmationModal"
      ref="confirmationModal"
    />
    <modal-wrapper ref="updateAttachmentNameModal">
      <update-attachment-name-modal
        :attachment-entity-type="selectedTreeElement.attachmentEntityType"
        :entities-id-array="entitiesIdArray"
        :attachment-id="attachmentId"
        :file-name="fileName"
        @attachmentNameUpdated="onAttachmentNameUpdated"
      />
    </modal-wrapper>
  </b-modal>
</template>

<script>
import VtThumbnail from '@/components/common/VtThumbnail'
import VtBubble from '@/components/common/VtBubble'
import PatientTree from '@/components/attachments/PatientTree'
import AddFileModal from '@/components/AddFileModal'
import patientAttachmentService from '@/api/resources/attachment/patientAttachmentService'
import requestAttachmentService from '@/api/resources/attachment/requestAttachmentService'
import caseAttachmentService from '@/api/resources/attachment/caseAttachmentService'
import sampleAttachmentService from '@/api/resources/attachment/sampleAttachmentService'
import portionAttachmentService from '@/api/resources/attachment/portionAttachmentService'
import subportionAttachmentService from '@/api/resources/attachment/subportionAttachmentService'
import ModalConfirmation from '@/components/modals/ModalConfirmation'
import UpdateAttachmentNameModal from '@/components/attachments/UpdateAttachmentNameModal'
import attachmentsMixin from '@/components/attachments/mixins/attachmentsMixin'
import complementaryAttachmentService from '@/api/resources/attachment/complementaryAttachmentService'
import { ViewerType, BROADCAST_ATTACHMENT_UPDATED_CHANNEL, BROADCAST_ATTACHMENT_UPDATED_CHANNEL_MESSAGE } from '@/modules/viewerModule/ViewerModule.models'
import { viewerService } from '@/modules/viewerModule/services/viewer.service'
import { ATTACHMENT_MIME_TYPES } from '@/api/resources/attachment/attachmentTypeService'
import CommonSubportionServices from '@/api/resources/common/commonSubportionServices'

export default {
    name: 'CommonAttachmentTreeModal',
    components: {
        VtThumbnail,
        VtBubble,
        PatientTree,
        ModalConfirmation,
        AddFileModal,
        UpdateAttachmentNameModal
    },
    mixins: [
        attachmentsMixin
    ],
    props: {
        initMode: { type: Object, default: () => ({ value: 'compact', icon: 'fal fa-list-ul' }) },
        id: { type: String },
        content: { type: String },
        okTitle: { type: String, default: 'global.close' },
        laboratoryId: { type: Number },
        patient: { type: Object, required: true },
        associatePictures: { type: Boolean }
    },
    data() {
        return {
            selectedTreeElement: {
                requestId: null,
                caseId: null,
                sampleId: null,
                portionId: null,
                subportionId: null,
                patientId: null,
                entityDisplay: null,
                elementIcon: 'fa-chart-network',
                attachments: []
            },
            entitiesIdArray: [],
            attachmentsChecked: [],
            btnPressed: false,
            mode: this.initMode,
            fileName: '',
            attachmentId: null,
            broadCastAttachmentUpdateChannel: null
        }
    },
    computed: {
        canBeAttached() {
            return this.selectedTreeElement.canBeAttached
        },
        canBeDeleted() {
            return this.selectedTreeElement.canBeAttached && this.attachmentsChecked.length
        },
        canBeEdited() {
            return this.selectedTreeElement.canBeAttached && (this.attachmentsChecked.length === 1)
        }
    },
    created() {
        this.broadCastAttachmentUpdateChannel = new BroadcastChannel(BROADCAST_ATTACHMENT_UPDATED_CHANNEL)
        this.broadCastAttachmentUpdateChannel.onmessage = () => {
            this.reloadView()
        }
    },
    beforeDestroy() {
        this.broadCastAttachmentUpdateChannel.close()
    },
    methods: {
        async reloadView() {
            await this.$refs.patientTree.showChild(this.selectedTreeElement.vueObject)
            this.$emit('attachments-updated')
            this.attachmentsChecked = []
        },
        async onAttachmentsAdded() {
            this.broadCastAttachmentUpdateChannel.postMessage(BROADCAST_ATTACHMENT_UPDATED_CHANNEL_MESSAGE)
            await this.reloadView()
        },
        async onAttachmentNameUpdated() {
            await this.reloadView()
        },
        getAttachmentsChecked() {
            this.attachmentsChecked = this.$refs.attachmentThumbnail ? this.$refs.attachmentThumbnail.filter(thumbnail => thumbnail.isSelected) : []
        },
        showUpdateFileNameModal() {
            this.entitiesIdArray = this.setEntitiesIdArray(this.selectedTreeElement, this.laboratoryId)
            this.fileName = this.attachmentsChecked[0].attachment.filename
            this.attachmentId = this.attachmentsChecked[0].attachment.attachmentId
            this.$refs.updateAttachmentNameModal.show()
        },
        async showPatient() {
            await this.$refs.patientTree.showPatient()
            this.$refs.attachmentTreeModal.show()
        },
        async showCase(caseId) {
            await this.$refs.patientTree.showCase(caseId)
            this.$refs.attachmentTreeModal.show()
        },
        async showSample(sampleId) {
            await this.$refs.patientTree.showSample(sampleId)
            this.$refs.attachmentTreeModal.show()
        },
        async showComplementary(complementaryId) {
            await this.$refs.patientTree.showComplementary(complementaryId)
            this.$refs.attachmentTreeModal.show()
        },
        async showPortion(portionId) {
            await this.$refs.patientTree.showPortion(portionId)
            this.$refs.attachmentTreeModal.show()
        },
        async showSubportion(subportionId) {
            await this.$refs.patientTree.showSubportion(subportionId)
            this.$refs.attachmentTreeModal.show()
        },
        hide() {
            this.$refs.attachmentTreeModal.hide()
        },
        patientTreeElementSelected(eventValue) {
            let entityLabel
            if (eventValue.subportion) {
                entityLabel = eventValue.subportion.supLabel
            } else if (eventValue.portion) {
                entityLabel = eventValue.portion.porLabel
            } else if (eventValue.sample) {
                entityLabel = eventValue.sample.samLabel
            } else if (eventValue.caseStudy) {
                entityLabel = eventValue.caseStudy.casCasenumber
            } else if (eventValue.patient) {
                entityLabel = eventValue.patient.name
            } else {
                entityLabel = eventValue.label
            }
            this.selectedTreeElement = {
                canBeAttached: eventValue.canBeAttached,
                complementaryId: eventValue.complementaryId,
                requestId: eventValue.requestId,
                caseId: eventValue.caseId,
                sampleId: (eventValue.sample ? eventValue.sample.samId : eventValue.sampleId),
                portionId: eventValue.portionId,
                subportionId: (eventValue.subportion ? eventValue.subportion.supId : undefined),
                entityLabel,
                patientId: eventValue.patientId,
                entityDisplay: eventValue.entityDisplay,
                elementIcon: eventValue.elementIcon,
                attachments: eventValue.attachments,
                userHasAttachOperation: eventValue.userHasAttachOperation,
                laboratoryId: eventValue.laboratoryId,
                attachmentEntityType: eventValue.attachmentEntityType,
                vueObject: eventValue
            }
        },
        showAddAttachmentModal() {
            this.entitiesIdArray = this.setEntitiesIdArray(this.selectedTreeElement, this.laboratoryId)
            this.$refs.addFileModalWrapper.show()
        },
        deleteAttachmentConfirmation() {
            this.$refs.confirmationModal.ask(this.deleteAttachments)
        },
        async deleteAttachments() {
            this.updateLoading(true)
            const selectedIds = []
            this.$refs.attachmentThumbnail.forEach(thumbnail => {
                if (thumbnail.isSelected) {
                    selectedIds.push(thumbnail.attachment.attachmentId)
                }
            })

            let deleteFunction = () => {
            }
            let errorMessage = ''

            if (this.selectedTreeElement.subportionId) {
                deleteFunction = (attachmentId) => {
                    return subportionAttachmentService.deleteAttachment(this.laboratoryId, this.selectedTreeElement.caseId, this.selectedTreeElement.sampleId, this.selectedTreeElement.portionId, this.selectedTreeElement.subportionId, attachmentId)
                }
                errorMessage = 'component.attachModal.error.subportionAttachmentDeleteError'
            } else if (this.selectedTreeElement.portionId) {
                deleteFunction = (attachmentId) => {
                    return portionAttachmentService.deleteAttachment(this.laboratoryId, this.selectedTreeElement.caseId, this.selectedTreeElement.sampleId, this.selectedTreeElement.portionId, attachmentId)
                }
                errorMessage = 'component.attachModal.error.portionAttachmentDeleteError'
            } else if (this.selectedTreeElement.complementaryId) {
                deleteFunction = (attachmentId) => {
                    return complementaryAttachmentService.deleteAttachment(this.laboratoryId, this.selectedTreeElement.caseId, this.selectedTreeElement.complementaryId, attachmentId)
                }
                errorMessage = 'component.attachModal.error.complementaryAttachmentError'
            } else if (this.selectedTreeElement.sampleId) {
                deleteFunction = (attachmentId) => {
                    return sampleAttachmentService.deleteAttachment(this.laboratoryId, this.selectedTreeElement.caseId, this.selectedTreeElement.sampleId, attachmentId)
                }
                errorMessage = 'component.attachModal.error.sampleAttachmentDeleteError'
            } else if (this.selectedTreeElement.caseId) {
                deleteFunction = (attachmentId) => {
                    return caseAttachmentService.deleteAttachment(this.laboratoryId, this.selectedTreeElement.caseId, attachmentId)
                }
                errorMessage = 'component.attachModal.error.caseAttachmentDeleteError'
            } else if (this.selectedTreeElement.requestId) {
                deleteFunction = (attachmentId) => {
                    return requestAttachmentService.deleteAttachment(this.laboratoryId, this.selectedTreeElement.requestId, attachmentId)
                }
                errorMessage = 'component.attachModal.error.requestAttachmentDeleteError'
            } else if (this.selectedTreeElement.patientId) {
                deleteFunction = (attachmentId) => {
                    return patientAttachmentService.deleteAttachment(this.selectedTreeElement.patientId, attachmentId)
                }
                errorMessage = 'component.attachModal.error.patientAttachmentDeleteError'
            }

            let errors = false
            for (let attachmentId of selectedIds) {
                const [err] = await this.toCall(deleteFunction(attachmentId))
                if (err) {
                    errors = true
                }
            }
            if (errors) {
                this.showErrorModalGlobal(this.$t(errorMessage))
            }
            await this.$refs.patientTree.showChild(this.selectedTreeElement.vueObject)
            this.$emit('attachments-updated')
            this.broadCastAttachmentUpdateChannel.postMessage(BROADCAST_ATTACHMENT_UPDATED_CHANNEL_MESSAGE)
            this.attachmentsChecked = []
            this.updateLoading(false)
        },
        async openAttachment(query) {
            if (query.mimeType.startsWith(ATTACHMENT_MIME_TYPES.IMAGE)) {
                if (query.portionId && !query.subportionId) {
                    window.open(query.url, '_blank')
                } else {
                    this.openViewer(query, ViewerType.STATIC_VIEWER)
                }
            } else if (query.mimeType === ATTACHMENT_MIME_TYPES.URI) {
                const subportionInfo = await this.$_toCallUnique(CommonSubportionServices.getSubportionInformation(this.selectedTreeElement.laboratoryId, this.selectedTreeElement.caseId, this.selectedTreeElement.sampleId, this.selectedTreeElement.portionId, this.selectedTreeElement.subportionId))
                let useInternalViewer = false
                if (subportionInfo && subportionInfo.scannerDevice) {
                    useInternalViewer = subportionInfo.scannerDevice.useInternalViewer ?? false
                }

                if (useInternalViewer) {
                    this.openViewer(query, ViewerType.WSI_VIEWER)
                } else {
                    const response = await this.$_toCallUnique(subportionAttachmentService.getUrlForOpenViewer(
                        query.laboratoryId, query.caseId, query.sampleId, query.portionId, query.subportionId, query.attachmentId))
                    if (response) {
                        this.$_openUrl({ url: response })
                    }
                }
            } else {
                window.open(query.url, '_blank')
            }
        },
        openViewer(query, type) {
            viewerService.openViewer({
                router: this.$router,
                viewerType: type,
                laboratoryId: query.laboratoryId,
                caseId: query.caseId,
                sampleId: query.sampleId,
                attachmentId: query.attachmentId,
                label: query.label,
                portionId: query.portionId,
                subportionId: query.subportionId
            })
        }
    }
}

</script>

<style lang="scss">

   .attachmentTreeModal .thumb-card.associated {
      border-color: vt-color(green-radiant);
   }

  .attachmentTreeModal .attach-body {
    padding: 0;
  }

  .attachmentTreeModal .thumb-list{
    height: calc(100vh - 190px);
    min-height: 400px;
    overflow-y: auto;
    overflow-x: hidden;
  }

  .attachmentTreeModal .thumb-list.thumb-list-pictures{
      height: calc(100vh - 240px);
      min-height: 400px;
  }

  .attachmentTreeModal .modal-body {
    padding: 0;
    border-radius: .3rem;
  }

  .attachmentTreeModal .patientCard .flex-btn {
    width: 4% !important;
  }

  .attachmentTreeModal .patientCard .title-container {
    margin-right: 2rem;
  }

  .attachmentTreeModal .patientCard i {
    font-size: 1.9rem !important;
  }

  .attachmentTreeModal .content .container {
    max-width: 100%;
  }

  .attachmentTreeModal .tree {
    border-right: 1px solid #dee2e6;
    background: vt-color(base-text,.90);
  }

  .attachmentTreeModal .attach-header {
    border-bottom: 1px solid #dee2e6;
  }

  .attachmentTreeModal .attach-content {
    height: calc(100vh - 235px);
    min-height: 400px;
    overflow: hidden;
    overflow-y: auto;
    margin-right: 5px;
  }

  .attachmentTreeModal .tree-content-attachments {
    height: calc(100vh - 155px);
    min-height: 400px;
    overflow: hidden;
    overflow-y: auto;
    margin-right: 0px;
    display: flex;
    flex-direction: column;
  }

   .attachmentTreeModal .tree-content-attachments span {
       @include text-truncate();
   }

  .attachmentTreeModal .tree .element .btn {
    background: vt-color(white-light);
    color: vt-color(dark-text);
    border-color: #dee2e6;
    border-radius: 2px;
    width: 100%;
    margin-bottom: 5px;
    box-shadow: 0px 1px 1px 0px vt-color(base-text, .7);
    font-size: font-size(very-small);
  }

  .attachmentTreeModal .tree .element .btn i {
    transition: .3s;
  }

  .attachmentTreeModal .tree .element .btn .attach-arrow i:hover {
    color: vt-color(primary);
  }

  .attachmentTreeModal .tree .element .btn .attach-arrow i {
    color: vt-color(base-text);
    font-size: font-size(tiny);
  }

  .attachmentTreeModal .tree .element .arrow {
    position: absolute;
    top: 12px;
    right: 12px;
    font-size: .9rem;
  }

  .attachmentTreeModal .tree .element .btn:hover {
    background: vt-color(light-primary);
    border-color: vt-color(alt-primary, .5);
  }

  .attach-arrow {
    padding: 0 3px;
  }

  .attach-display {
    display: none;
  }

  .attach-pressed {
    background: vt-color(primary) !important;
    border-radius: 3px !important;
    color: #FFF !important;
  }

  .attach-pressed i {
    color: #FFF !important;
  }

  .case-separator{
    border-top: 2px solid vt-color(base-text, .7);
  }

  .attachmentTreeModal .sidepanel {
    border-left: 1px solid #dee2e6;
    background: #FFF;
    width: 300px;
    position: absolute;
    right: -300px;
    height: 100%;
    transition: .3s;
    box-shadow: 0px 3px 5px 0px #9c9c9c;
  }

  .attachmentTreeModal .sidepanel.animation {
    right: 0;
  }

  .attachmentTreeModal .sidepanel .header {
    border-bottom: 1px solid #dee2e6;
  }

  .attachmentTreeModal .sidepanel .title {
    color: vt-color(primary);
  }

  .attachmentTreeModal .sidepanel .content {
    height: 100%;
    overflow: hidden;
    overflow-y: scroll;
  }

  .attachmentTreeModal .sidepanel .file {
    border-bottom: 1px solid #dee2e6;
  }

  .attachmentTreeModal .sidepanel .file .others {
    color: grey;
    font-size: .9rem;
  }

  .attachmentTreeModal .sidepanel .element .col-md-4 {
    max-width: 100% !important;
    padding-left: 10px;
    margin-top: 5px;
    margin-bottom: 0 !important;
  }

  .attachmentTreeModal .tree .element .btn.attach-pressed {
    border: 1px solid vt-color(primary);
  }

</style>
